export type LotRecomputeProgress = {
  phase: 'loading' | 'computing' | 'writing' | 'pruning'
  pass: number
  partitionsDone: number
  partitionsTotal: number
  rowsWritten?: number
  rowsTotal?: number
}

export type LotRecomputeResult = {
  partitions: number
  failed: number
}

// BullMQ job states; `unknown` is a job pruned before it was read.
export type LotRecomputeStatus =
  | 'waiting'
  | 'active'
  | 'delayed'
  | 'prioritized'
  | 'waiting-children'
  | 'completed'
  | 'failed'
  | 'unknown'

export type LotRecomputeJob = {
  jobId: string | null
  status: LotRecomputeStatus
  progress?: LotRecomputeProgress | null
  result?: LotRecomputeResult | null
}

/** `settled` is transient: present only on the poll that saw the transition. */
export type LotRecomputePoll = {
  job: LotRecomputeJob | null
  settled: LotRecomputeJob | null
}

/**
 * A change that landed mid-run is requeued under a new id once the run
 * finishes; `nextJobId` names it so the poller can follow it.
 */
export type LotRecomputeFinalState = LotRecomputeJob & {
  nextJobId: string | null
}

const SETTLED_STATUSES = [
  'completed',
  'failed',
  'unknown',
] satisfies LotRecomputeStatus[]

export const isLotRecomputeSettled = (job: LotRecomputeJob) =>
  (SETTLED_STATUSES as LotRecomputeStatus[]).includes(job.status)

const queuedJob = (jobId: string): LotRecomputeJob => ({
  jobId,
  status: 'waiting',
  progress: null,
  result: null,
})

/**
 * A followed job settles either by coming back with a settled status or by
 * leaving the per-org lookup; either way its final state is read once by id.
 */
export const resolveLotRecomputePoll = async ({
  followed,
  current,
  readFinalState,
}: {
  followed: LotRecomputeJob | null
  current: LotRecomputeJob | null
  readFinalState: (jobId: string) => Promise<LotRecomputeFinalState>
}): Promise<LotRecomputePoll> => {
  const isCurrentSettled = current !== null && isLotRecomputeSettled(current)
  const settledJobId = isCurrentSettled
    ? current.jobId
    : followed?.jobId && followed.jobId !== current?.jobId
      ? followed.jobId
      : null

  if (settledJobId === null) {
    return isCurrentSettled
      ? { job: null, settled: current }
      : { job: current, settled: null }
  }

  const { nextJobId, ...settled } = await readFinalState(settledJobId)
  // The successor is read a moment after the org lookup, so it can name a job
  // the lookup did not yet see; without it the poll would stop on a rebuild
  // that is still to come.
  const successor = nextJobId ? queuedJob(nextJobId) : null
  const job = (isCurrentSettled ? null : current) ?? successor
  return { job, settled }
}

/**
 * One status line naming the recompute, so it stands alone in the banner
 * sentence and in the button's tooltip.
 */
export const describeLotRecomputeProgress = (job: LotRecomputeJob): string => {
  if (job.status === 'delayed') return 'Recompute retrying shortly'
  const progress = job.progress
  if (!progress || job.status !== 'active') return 'Recompute queued'

  const count = (done: number, total: number, noun: string) =>
    `${done.toLocaleString()} of ${total.toLocaleString()} ${noun}${total === 1 ? '' : 's'}`

  switch (progress.phase) {
    case 'loading':
      return 'Recomputing lots, loading transfers'
    case 'computing': {
      const position = count(
        progress.partitionsDone,
        progress.partitionsTotal,
        'token',
      )
      // Pass 0 covers every token; later passes only revisit those a carry
      // landed in, so the counter restarts and the label says why.
      return progress.pass > 0
        ? `Recomputing carried lots, ${position} done`
        : `Recomputing lots, ${position} done`
    }
    case 'writing':
      return progress.rowsTotal !== undefined
        ? `Recomputing lots, saving ${count(progress.rowsWritten ?? 0, progress.rowsTotal, 'row')}`
        : 'Recomputing lots, saving results'
    case 'pruning':
      return 'Recomputing lots, cleaning up'
  }
}
